import React from 'react';
import { Button } from '../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { motion } from 'framer-motion';
import {
  ScrollText,
  User,
  Clock,
  Trash2,
  RefreshCw,
  Filter,
} from 'lucide-react';

export const LogsTab = ({
  isItalian,
  activityLogs,
  loadingLogs,
  logFilter,
  setLogFilter,
  actionTypes,
  fetchActivityLogs,
  handleClearOldLogs,
  handleClearAllLogs,
  getActionLabel,
  formatLogDate,
}) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="bg-[#0a0d15] border border-white/10"
    >
      <div className="p-4 border-b border-white/10 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <ScrollText size={20} className="text-[#FFEA00]" />
          <h3 className="font-heading text-lg uppercase tracking-wider text-white">
            {isItalian ? 'Log Attività' : 'Activity Logs'}
          </h3>
          <span className="text-xs text-slate-500">({activityLogs.length})</span>
        </div>
        <div className="flex gap-3 items-center">
          <Select
            value={logFilter}
            onValueChange={(val) => {
              setLogFilter(val);
              fetchActivityLogs(val);
            }}
            modal={false}
          >
            <SelectTrigger className="w-48 bg-[#050508] border-white/10 text-white" data-testid="log-filter">
              <Filter size={14} className="mr-2" />
              <SelectValue placeholder={isItalian ? 'Filtra per azione' : 'Filter by action'} />
            </SelectTrigger>
            <SelectContent className="bg-[#0a0d15] border-white/10 z-[100]">
              <SelectItem value="all" className="text-white hover:bg-white/10 cursor-pointer">
                {isItalian ? 'Tutte le azioni' : 'All actions'}
              </SelectItem>
              {actionTypes.map((action) => (
                <SelectItem key={action} value={action} className="text-white hover:bg-white/10 cursor-pointer">
                  {getActionLabel(action).label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            onClick={() => fetchActivityLogs(logFilter)}
            variant="outline"
            className="border-white/10"
            size="sm"
          >
            <RefreshCw size={14} className="mr-2" />
            {isItalian ? 'Aggiorna' : 'Refresh'}
          </Button>
          <Button
            onClick={handleClearOldLogs}
            variant="outline"
            className="border-orange-500 text-orange-500 hover:bg-orange-500/10"
            size="sm"
            title={isItalian ? 'Elimina log più vecchi di 30 giorni' : 'Delete logs older than 30 days'}
          >
            <Clock size={14} className="mr-2" />
            {isItalian ? '>30 giorni' : '>30 days'}
          </Button>
          <Button
            onClick={handleClearAllLogs}
            variant="outline"
            className="border-[#FF1744] text-[#FF1744] hover:bg-[#FF1744]/10"
            size="sm"
            title={isItalian ? 'Elimina TUTTI i log' : 'Delete ALL logs'}
          >
            <Trash2 size={14} className="mr-2" />
            {isItalian ? 'Elimina Tutti' : 'Clear All'}
          </Button>
        </div>
      </div>

      <div className="p-4 max-h-[600px] overflow-y-auto">
        {loadingLogs ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin w-8 h-8 border-2 border-[#FFEA00] border-t-transparent rounded-full" />
          </div>
        ) : activityLogs.length === 0 ? (
          <div className="text-center py-12">
            <ScrollText size={48} className="mx-auto text-slate-600 mb-4" />
            <p className="text-slate-400">
              {isItalian ? 'Nessun log trovato' : 'No logs found'}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {activityLogs.map((log, idx) => {
              const actionInfo = getActionLabel(log.action);
              return (
                <div
                  key={`${log.log_id}-${idx}`}
                  className="flex items-start gap-4 p-3 bg-[#050508] border border-white/10 hover:border-white/20 transition-colors"
                >
                  <div className="flex-shrink-0 pt-1">
                    <Clock size={14} className="text-slate-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span className={`px-2 py-0.5 text-xs font-bold uppercase rounded ${actionInfo.color}`}>
                        {actionInfo.label}
                      </span>
                      <span className="text-xs text-slate-500">
                        {formatLogDate(log.created_at)}
                      </span>
                    </div>
                    <p className="text-sm text-white">{log.details}</p>
                    <div className="flex items-center gap-4 mt-1 text-xs text-slate-500">
                      <span>
                        <User size={12} className="inline mr-1" />
                        {log.actor_name}
                      </span>
                      <span className="px-1.5 py-0.5 bg-slate-700/50 rounded text-slate-400">
                        {log.actor_role}
                      </span>
                      {log.target_name && (
                        <span className="text-slate-400">
                          Target: {log.target_name}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </motion.div>
  );
};
